import { DollarSign, PieChart, Plus, PlusCircle } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Image } from '@unistocks/components/basic/image';
import { ScrollViewStyles } from '@unistocks/components/basic/scrollview';
import { FabMenu, FabMenuGroup } from '@unistocks/components/floating-action-button-menu/fab-menu';
import { EmptyList } from '@unistocks/components/no-data/empty-list';
import { Page } from '@unistocks/components/page/page';
import { FullScreenSheet } from '@unistocks/components/sheet/full-screen-sheet';
import { Sheet } from '@unistocks/components/sheet/sheet';
import { toast } from '@unistocks/components/toast';
import { AssetSettingsProvider } from '@unistocks/features/portfolios/components/asset-settings/asset-settings-provider';
import { AddOrUpdateSectionForm } from '@unistocks/features/portfolios/components/forms/add-or-update-section-form';
import { AddTransactionForm } from '@unistocks/features/portfolios/components/forms/add-transaction-form';
import { PortfolioHeaderRight } from '@unistocks/features/portfolios/components/header/header-right';
import { PortfolioHeaderTitle } from '@unistocks/features/portfolios/components/header/header-title';
import { PortfolioDetails } from '@unistocks/features/portfolios/components/portfolio-details/portfolio-details';
import {
  getPortfolioTableItemType,
  keyExtractor,
  PortfolioTableFooter,
  renderPortfolioTableItem,
  usePortfolioTableData
} from '@unistocks/features/portfolios/components/portfolio-table/portfolio-table';
import { PortfolioAddButtonEvent } from '@unistocks/features/portfolios/models/portfolio-menu';
import {
  portfoliosGetQueryKey,
  usePortfoliosCreateSectionMutation,
  usePortfoliosGetQuery
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import {
  useTransactionsCreateMutation
} from '@unistocks/proxy/axios-client/TransactionsQuery';
import { UNISTOCKS_ARROW } from '@unistocks/shared/consts';
import { t } from '@unistocks/shared/localization/index.ts';
import { HasId } from '@unistocks/types/has-id';
import { useRouter } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';
import { Circle, isWeb, Separator } from 'tamagui';
import { invalidatePortfolioQueriesById } from './portfolio-utils';

export const Portfolio: React.FC<HasId> = ({ id }) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data: portfolio, isSuccess, isError, isRefetching } = usePortfoliosGetQuery({ id });
  const [transactionModalOpen, setTransactionModalOpen] = useState(false);
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const title = isSuccess
    ? portfolio.name
    : isError && !isWeb
      ? t('::SelectWhat', { 0: t('::Portfolio').toLowerCase() })
      : null;

  const onRefresh = () => {
    invalidatePortfolioQueriesById(queryClient, id);
  };

  const tableData = usePortfolioTableData(portfolio);
  // const stickyHeaderIndices = useStickyHeaderIndicies(tableData);

  const { mutateAsync: addSection } = usePortfoliosCreateSectionMutation(id, {
    onMutate: () => {
      setSectionModalOpen(false);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portfoliosGetQueryKey(id) });
    }
  });

  const { mutateAsync: addTransaction } = useTransactionsCreateMutation({
    onMutate: () => {
      setTransactionModalOpen(false);
    },
    onSuccess: () => {
      toast.success(t('::Transaction:Created'));
      queryClient.invalidateQueries();
    }
  });

  const fabMenuItems: FabMenuGroup<PortfolioAddButtonEvent>[] = useMemo(() => {
    if (!portfolio) {
      return [];
    }

    const sectionItems: FabMenuGroup = {
      key: 1,
      groupHeader: `${t('::AddWhat', { 0: t('::Holdings').toLocaleLowerCase() })} ${t(
        '::AddSuffix'
      )}`,
      value: portfolio.sections.map(section => ({
        key: section.id,
        title: section.name,
        icon: (
          <Circle bg={'$color5'} style={{ marginRight: 0 }}>
            <Plus color={'$success'} />
          </Circle>
        ),
        value: {
          type: 'asset',
          params: { sectionId: section.id }
        }
      }))
    };

    return [
      sectionItems,
      {
        key: 2,
        value: [
          {
            key: 'category',
            title: t('::AddWhat', { 0: t('::Section').toLocaleLowerCase() }),
            icon: ({ size }) => (
              <Image
                source={UNISTOCKS_ARROW}
                style={{ width: size, height: size, overflow: 'visible' }}
              />
            ),
            value: { type: 'category' }
          }
        ]
      },
      {
        key: 3,
        value: [
          {
            key: 'transaction',
            title: t('::AddWhat', { 0: t('::Transaction').toLocaleLowerCase() }),
            icon: <DollarSign color={'$green11'} />,
            value: { type: 'transaction' }
          }
        ]
      }
    ];
  }, [portfolio]);

  const onMenuItemPress = useCallback(
    (item: PortfolioAddButtonEvent) => {
      switch (item.type) {
        case 'asset':
          router.push({
            pathname: '/(tabs)/portfolios/[id]/add-asset',
            params: { id, sectionId: item.params?.sectionId }
          });
          break;

        case 'category':
          setSectionModalOpen(true);
          break;

        case 'transaction':
          setTransactionModalOpen(true);
          break;
      }
    },
    [id, router]
  );

  return (
    <AssetSettingsProvider portfolio={portfolio}>
      <Page
        queryState={{
          queryKeys: [portfoliosGetQueryKey(id)],
          loadingText: t('::Portfolio:Loading')
        }}
      >
        <Page.VirtualList
          data={tableData}
          extraData={portfolio}
          keyExtractor={keyExtractor}
          getItemType={getPortfolioTableItemType}
          ListHeaderComponent={
            isSuccess && tableData.length ? (
              <Page.Content pt={0} $xl={{ px: '$4' }}>
                <PortfolioDetails portfolio={portfolio} />
              </Page.Content>
            ) : null
          }
          renderItem={renderPortfolioTableItem}
          ListFooterComponent={
            isSuccess && tableData.length ? <PortfolioTableFooter portfolio={portfolio} /> : null
          }
          ItemSeparatorComponent={Separator}
          ListEmptyComponent={
            isSuccess ? (
              <EmptyList
                noDataText={t('::Portfolio:PortfolioTable:NoData')}
                noDataIcon={PieChart}
              />
            ) : null
          }
          refreshing={isRefetching}
          onRefresh={onRefresh}
          contentContainerStyle={ScrollViewStyles.contentContainerStyle}
          headerTitle={<PortfolioHeaderTitle>{title}</PortfolioHeaderTitle>}
          headerIcon={isSuccess ? <PortfolioHeaderRight id={id} /> : undefined}
          headerBackArrow={false}
        />

        {/* Add menu */}
        <FabMenu onItemPress={onMenuItemPress} items={fabMenuItems} />

        {/* Add section */}
        <Sheet
          open={sectionModalOpen}
          onOpenChange={setSectionModalOpen}
          headerTitle={t('::AddWhat', { 0: t('::Category').toLowerCase() })}
          headerIcon={<PlusCircle color={'$success'} />}
          index={1}
        >
          {portfolio ? (
            <AddOrUpdateSectionForm portfolio={portfolio} onSubmit={addSection} />
          ) : null}
        </Sheet>

        {/* Add transaction */}
        <FullScreenSheet
          open={transactionModalOpen}
          onOpenChange={setTransactionModalOpen}
          headerTitle={t('::AddWhat', { 0: t('::Transaction').toLowerCase() })}
          headerIcon={<PlusCircle color={'$success'} />}
        >
          {portfolio ? (
            <AddTransactionForm portfolio={portfolio} onSubmit={addTransaction} />
          ) : null}
        </FullScreenSheet>
      </Page>
    </AssetSettingsProvider>
  );
};

Portfolio.displayName = 'Portfolio';
